import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import LanguageToggle from '../../components/LanguageToggle';
import VocationWorksLogo from '../../components/VocationWorksLogo';

const NAVY     = '#0D1B2A';
const AMBER    = '#F5A623';
const WHITE    = '#FFFFFF';
const BG       = '#F5F6F8';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

const inp = {
  width: '100%', padding: '11px 13px', fontSize: '1rem', // 16px: no iOS zoom on focus
  border: '1.5px solid #E2E6EB', borderRadius: '8px',
  color: NAVY, outline: 'none', backgroundColor: WHITE, fontFamily: FONT_BODY,
};
const sel = { ...inp, cursor: 'pointer' };
const txt = { ...inp, resize: 'vertical', minHeight: '90px' };
const lbl = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: NAVY, marginBottom: '5px' };
const fld = { marginBottom: '1.1rem' };
const g2  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.1rem' };
const card = {
  backgroundColor: WHITE, borderRadius: '16px',
  boxShadow: '0 2px 24px rgba(0,0,0,0.07)',
  padding: '2rem', marginBottom: '1.25rem',
};

const STATES = ['IL', 'IN', 'WI', 'MI', 'OH', 'MO', 'IA', 'MN'];
// Read-only look for the invited email (prefilled from the invite).
const lockedInp = { ...inp, backgroundColor: '#EEF0F3', color: 'rgba(13,27,42,0.7)', cursor: 'not-allowed' };
const checkRow = {
  display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '0.875rem',
  color: 'rgba(13,27,42,0.75)', lineHeight: 1.5, minHeight: '44px', padding: '8px 0',
};
const checkBox = { marginTop: '2px', accentColor: AMBER, width: '20px', height: '20px', flexShrink: 0 };

// Terms of Service / Privacy Policy + "I am 18 or older" -- the same two required checkboxes as
// the worker, employer and union signups, used by every invite type on this page.
function TermsAndAge({ form, set, t }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={checkRow}>
        <input type="checkbox" checked={form.agreed} onChange={e => set('agreed', e.target.checked)} style={checkBox} />
        <span>
          {t('worker.signup.step1.agreePrefix')}{' '}
          <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" style={{ color: AMBER, textDecoration: 'none', fontWeight: 600 }}>{t('common.termsOfService')}</a>
          {' '}{t('worker.signup.step1.agreeAnd')}{' '}
          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: AMBER, textDecoration: 'none', fontWeight: 600 }}>{t('common.privacyPolicy')}</a>
          {t('worker.signup.step1.agreeSuffix') && ` ${t('worker.signup.step1.agreeSuffix')}`}
        </span>
      </label>
      <label style={checkRow}>
        <input type="checkbox" checked={form.ageConfirmed} onChange={e => set('ageConfirmed', e.target.checked)} style={checkBox} />
        <span>{t('worker.signup.step1.ageConfirmLabel')}</span>
      </label>
    </div>
  );
}

function Shell({ children }) {
  const navigate = useNavigate();

  useEffect(() => {
    const link = document.createElement('link');
    link.rel  = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);

    const style = document.createElement('style');
    style.textContent = `
      *, *::before, *::after { box-sizing: border-box; }
      body { background-color: ${BG}; font-family: ${FONT_BODY}; color: ${NAVY}; margin: 0; }
      input, select, textarea { font-family: ${FONT_BODY}; }
      input::placeholder, textarea::placeholder { color: rgba(13,27,42,0.32); }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div style={{ fontFamily: FONT_BODY, backgroundColor: BG, minHeight: '100dvh' }}>
      <nav style={{
        position: 'sticky', top: 0, zIndex: 200, height: '60px', backgroundColor: NAVY,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 2rem',
      }}>
        <div onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
          <VocationWorksLogo variant="reversed" size={36} />
        </div>
        <LanguageToggle />
      </nav>
      <div style={{ maxWidth: '680px', margin: '0 auto', padding: '2.5rem 1.25rem' }}>
        {children}
      </div>
    </div>
  );
}

export default function OrganizationJoin() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code') || '';

  const [loading, setLoading]   = useState(true);
  const [invite, setInvite]     = useState(null);
  const [orgName, setOrgName]   = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState('');

  const [form, setForm] = useState({
    fullName: '', email: '', password: '', confirmPassword: '',
    orgName: '', city: '', state: 'IL', about: '',
    // Member-only fields, mirroring WorkerStep1's exact field set (split first/last name,
    // phone, zip, ToS + age checkboxes) since a member is created as a normal worker account.
    firstName: '', lastName: '', phone: '', zip: '', agreed: false, ageConfirmed: false,
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  // The email the invite was sent to, when the invite has one: prefilled and read-only, so the
  // account is created for the address that was actually invited.
  const [invitedEmail, setInvitedEmail] = useState('');

  useEffect(() => {
    const lookup = async () => {
      if (!code) { setInvite(null); setLoading(false); return; }
      const { data, error: lookupError } = await supabase.rpc('lookup_invite', { p_code: code });
      const result = Array.isArray(data) ? data[0] : data;

      if (lookupError || !result || result.status !== 'pending') {
        setInvite(null);
        setLoading(false);
        return;
      }

      setInvite(result);
      setOrgName(result.org_name || '');
      const email = (result.email || '').trim();
      if (email) { setInvitedEmail(email); setForm(p => ({ ...p, email })); }
      setLoading(false);
    };
    lookup();
  }, [code]);

  const validateCommon = () => {
    if (!form.fullName.trim() || !form.email.trim() || !form.password || !form.confirmPassword) {
      setError(t('union.signup.step1.requiredFieldsAlert'));
      return false;
    }
    if (form.password.length < 8) {
      setError(t('union.signup.step1.passwordLengthAlert'));
      return false;
    }
    if (form.password !== form.confirmPassword) {
      setError(t('employer.signup.step1.passwordMismatchAlert'));
      return false;
    }
    if (!form.agreed) {
      setError(t('worker.signup.step1.agreeAlert'));
      return false;
    }
    if (!form.ageConfirmed) {
      setError(t('worker.signup.step1.ageAlert'));
      return false;
    }
    return true;
  };

  const handleSubmitFounding = async () => {
    setError('');
    if (!validateCommon()) return;
    if (!form.orgName.trim() || !form.city.trim() || !form.state.trim()) {
      setError(t('union.signup.step1.requiredFieldsAlert'));
      return;
    }

    setSubmitting(true);

    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: { data: { role: 'organization', full_name: form.fullName } },
    });
    if (signUpError) { setError(signUpError.message); setSubmitting(false); return; }

    const userId = signUpData?.session?.user?.id || signUpData?.user?.id;
    if (!userId) { setError(t('organization.join.couldNotCreateAccountError')); setSubmitting(false); return; }

    const { error: redeemError } = await supabase.rpc('redeem_founding_invite', {
      p_code: code,
      p_org_name: form.orgName.trim(),
      p_city: form.city.trim(),
      p_state: form.state,
      p_about: form.about.trim() || null,
    });
    setSubmitting(false);
    if (redeemError) {
      // The invite was invalid or got used in the time between page load and submit --
      // same "invalid invite" state as an initial bad code, not a form-field error.
      setInvite(null);
      return;
    }

    navigate('/organization/dashboard');
  };

  const handleSubmitStaff = async () => {
    setError('');
    if (!validateCommon()) return;

    setSubmitting(true);

    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: { data: { role: 'organization', full_name: form.fullName } },
    });
    if (signUpError) { setError(signUpError.message); setSubmitting(false); return; }

    const userId = signUpData?.session?.user?.id || signUpData?.user?.id;
    if (!userId) { setError(t('organization.join.couldNotCreateAccountError')); setSubmitting(false); return; }

    const { error: redeemError } = await supabase.rpc('redeem_staff_invite', { p_code: code });
    setSubmitting(false);
    if (redeemError) {
      // Same rationale as the founding path -- an invalid/already-used invite at this
      // point is the "invalid invite" state, not a form-field error.
      setInvite(null);
      return;
    }

    navigate('/organization/dashboard');
  };

  const handleSubmitMember = async () => {
    setError('');
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || !form.password || !form.confirmPassword) {
      setError(t('union.signup.step1.requiredFieldsAlert'));
      return;
    }
    if (form.password.length < 8) {
      setError(t('union.signup.step1.passwordLengthAlert'));
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError(t('employer.signup.step1.passwordMismatchAlert'));
      return;
    }
    if (!form.agreed) {
      setError(t('worker.signup.step1.agreeAlert'));
      return;
    }
    if (!form.ageConfirmed) {
      setError(t('worker.signup.step1.ageAlert'));
      return;
    }

    setSubmitting(true);

    // Same account-creation shape as WorkerStep1 -- a member is just a normal worker
    // account, so it gets created the exact same way (auth.signUp + workers upsert).
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: { data: { first_name: form.firstName, last_name: form.lastName } },
    });
    if (signUpError) { setError(signUpError.message); setSubmitting(false); return; }

    const userId = signUpData?.session?.user?.id || signUpData?.user?.id;
    if (!userId) { setError(t('organization.join.couldNotCreateAccountError')); setSubmitting(false); return; }

    const { error: workerError } = await supabase.from('workers').upsert({
      id: userId,
      first_name: form.firstName,
      last_name: form.lastName,
      email: form.email,
      phone: form.phone,
      zip_code: form.zip,
    }, { onConflict: 'id' });
    if (workerError) { setError(workerError.message); setSubmitting(false); return; }

    const { error: redeemError } = await supabase.rpc('redeem_member_invite', { p_code: code });
    if (redeemError) {
      // redeem_member_invite reads auth.uid() internally to attach the membership, which is
      // exactly why signUp had to happen before this call -- but that means a redemption
      // failure here (invite already used by a concurrent tab, expired between page load and
      // submit, etc.) would otherwise leave a real, orphaned auth account + workers row behind
      // with no organization attached and no way back in. Roll it back with the same
      // self-service RPC DeleteAccountModal already uses for real worker account deletion,
      // rather than just showing "invalid invite" and hiding that a live account was created.
      const { error: cleanupError } = await supabase.rpc('delete_own_worker_account');
      await supabase.auth.signOut();
      setSubmitting(false);
      if (cleanupError) {
        // The rollback itself failed -- this is the one case that can't be silently recovered,
        // so say so plainly with the email rather than pretending nothing happened.
        setError(t('organization.join.redeemFailedContactSupportError', { email: form.email }));
        return;
      }
      setInvite(null);
      return;
    }
    setSubmitting(false);

    // A member is a worker first, so they continue through the normal worker signup from
    // Step 2 (location, trade, certifications). Step 1's fields were collected by this form
    // and saved above; without the remaining steps there's no worker_profiles/worker_trades
    // row and nothing to match jobs against.
    navigate('/worker/signup/personal');
  };

  if (loading) {
    return (
      <Shell>
        <div style={{ ...card, textAlign: 'center' }}>
          <p style={{ fontSize: '0.9rem', color: 'rgba(13,27,42,0.5)' }}>{t('organization.join.checkingInvite')}</p>
        </div>
      </Shell>
    );
  }

  if (!invite) {
    return (
      <Shell>
        <div style={{ ...card, textAlign: 'center' }}>
          <h1 style={{ fontFamily: FONT_HEAD, fontSize: '1.9rem', color: NAVY, letterSpacing: '0.03em', marginBottom: '0.75rem' }}>
            {t('organization.join.invalidHeading')}
          </h1>
          <p style={{ fontSize: '0.95rem', color: 'rgba(13,27,42,0.6)', lineHeight: 1.6, marginBottom: '1.5rem' }}>
            {t('organization.join.invalidBody')}
          </p>
          <span
            onClick={() => navigate('/organization/login')}
            style={{ color: AMBER, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}
          >
            {t('organization.join.goToSignInLink')}
          </span>
        </div>
      </Shell>
    );
  }

  if (invite.invite_type === 'member') {
    return (
      <Shell>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: AMBER, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '1rem' }}>
          {t('organization.join.joiningEyebrow')}
        </div>

        <div style={card}>
          <h1 style={{ fontFamily: FONT_HEAD, fontSize: '2.1rem', color: NAVY, letterSpacing: '0.03em', marginBottom: '0.35rem' }}>
            {t('organization.join.memberHeading', { orgName: orgName ? orgName.toUpperCase() : t('organization.join.memberHeadingFallback') })}
          </h1>
          <p style={{ fontSize: '0.9rem', color: 'rgba(13,27,42,0.52)', marginBottom: '1.75rem', lineHeight: 1.5 }}>
            {t('organization.join.memberSubtitle', { orgName: orgName || t('organization.join.memberSubtitleFallback') })}
          </p>

          <div style={g2}>
            <div>
              <label style={lbl}>{t('worker.signup.step1.firstNameLabel')}</label>
              <input style={inp} placeholder="Maria" value={form.firstName} onChange={e => set('firstName', e.target.value)} />
            </div>
            <div>
              <label style={lbl}>{t('worker.signup.step1.lastNameLabel')}</label>
              <input style={inp} placeholder="Gonzalez" value={form.lastName} onChange={e => set('lastName', e.target.value)} />
            </div>
          </div>

          <div style={fld}>
            <label style={lbl}>{t('worker.signup.step1.emailLabel')}</label>
            <input style={invitedEmail ? lockedInp : inp} type="email" placeholder="you@email.com" value={form.email} readOnly={!!invitedEmail} aria-readonly={!!invitedEmail || undefined} onChange={e => { if (!invitedEmail) set('email', e.target.value); }} />
          </div>

          <div style={g2}>
            <div>
              <label style={lbl}>{t('worker.signup.step1.passwordLabel')}</label>
              <input style={inp} type="password" placeholder={t('worker.signup.step1.passwordPlaceholder')} value={form.password} onChange={e => set('password', e.target.value)} />
            </div>
            <div>
              <label style={lbl}>{t('worker.signup.step1.confirmPasswordLabel')}</label>
              <input style={inp} type="password" placeholder={t('worker.signup.step1.confirmPasswordPlaceholder')} value={form.confirmPassword} onChange={e => set('confirmPassword', e.target.value)} />
            </div>
          </div>

          <div style={g2}>
            <div>
              <label style={lbl}>{t('worker.signup.step1.phoneLabel')}</label>
              <input style={inp} type="tel" placeholder="(312) 555-0100" value={form.phone} onChange={e => set('phone', e.target.value)} />
            </div>
            <div>
              <label style={lbl}>{t('worker.signup.step1.zipLabel')}</label>
              <input style={inp} placeholder="60601" value={form.zip} onChange={e => set('zip', e.target.value)} />
            </div>
          </div>

          <TermsAndAge form={form} set={set} t={t} />

          {error && (
            <p style={{ fontSize: '0.85rem', color: '#DC2626', marginBottom: '1rem' }}>{error}</p>
          )}

          <button
            onClick={handleSubmitMember}
            disabled={submitting}
            style={{
              backgroundColor: AMBER, color: NAVY, border: 'none', borderRadius: '8px',
              padding: '13px 30px', fontWeight: 700, fontSize: '1rem',
              cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1,
              fontFamily: FONT_BODY, width: '100%',
            }}
          >
            {submitting ? t('organization.join.settingUpButton') : t('organization.join.joinButton')}
          </button>
        </div>
      </Shell>
    );
  }

  const isFounding = invite.invite_type === 'founding';

  return (
    <Shell>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: AMBER, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '1rem' }}>
        {isFounding ? t('organization.join.foundingEyebrow') : t('organization.join.joiningEyebrow')}
      </div>

      <div style={card}>
        <h1 style={{ fontFamily: FONT_HEAD, fontSize: '2.1rem', color: NAVY, letterSpacing: '0.03em', marginBottom: '0.35rem' }}>
          {isFounding
            ? t('organization.join.foundingHeading')
            : t('organization.join.memberHeading', { orgName: orgName ? orgName.toUpperCase() : t('organization.join.memberHeadingFallback') })}
        </h1>
        <p style={{ fontSize: '0.9rem', color: 'rgba(13,27,42,0.52)', marginBottom: '1.75rem', lineHeight: 1.5 }}>
          {isFounding
            ? t('organization.join.foundingSubtitle')
            : t('organization.join.staffSubtitle', { orgName: orgName || t('organization.join.staffSubtitleFallback') })}
        </p>

        {/* Personal account */}
        <div style={fld}>
          <label style={lbl}>{t('organization.join.fullNameLabel')}</label>
          <input style={inp} placeholder={t('employer.signup.step1.fullNamePlaceholder')} value={form.fullName} onChange={e => set('fullName', e.target.value)} />
        </div>

        <div style={fld}>
          <label style={lbl}>{t('worker.signup.step1.emailLabel')}</label>
          <input style={invitedEmail ? lockedInp : inp} type="email" placeholder="you@organization.org" value={form.email} readOnly={!!invitedEmail} aria-readonly={!!invitedEmail || undefined} onChange={e => { if (!invitedEmail) set('email', e.target.value); }} />
        </div>

        <div style={g2}>
          <div>
            <label style={lbl}>{t('worker.signup.step1.passwordLabel')}</label>
            <input style={inp} type="password" placeholder={t('worker.signup.step1.passwordPlaceholder')} value={form.password} onChange={e => set('password', e.target.value)} />
          </div>
          <div>
            <label style={lbl}>{t('worker.signup.step1.confirmPasswordLabel')}</label>
            <input style={inp} type="password" placeholder={t('worker.signup.step1.confirmPasswordPlaceholder')} value={form.confirmPassword} onChange={e => set('confirmPassword', e.target.value)} />
          </div>
        </div>

        {isFounding && (
          <>
            <div style={{ borderTop: '1px solid rgba(13,27,42,0.08)', margin: '1.5rem 0' }} />

            <div style={fld}>
              <label style={lbl}>{t('organization.join.orgNameLabel')}</label>
              <input style={inp} placeholder={t('organization.join.orgNamePlaceholder')} value={form.orgName} onChange={e => set('orgName', e.target.value)} />
            </div>

            <div style={g2}>
              <div>
                <label style={lbl}>{t('organization.join.cityLabel')}</label>
                <input style={inp} placeholder={t('organization.join.cityPlaceholder')} value={form.city} onChange={e => set('city', e.target.value)} />
              </div>
              <div>
                <label style={lbl}>{t('organization.join.stateLabel')}</label>
                <select style={sel} value={form.state} onChange={e => set('state', e.target.value)}>
                  {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div style={fld}>
              <label style={lbl}>{t('organization.join.aboutLabel')} <span style={{ fontWeight: 400, color: 'rgba(13,27,42,0.45)' }}>{t('organization.join.aboutOptional')}</span></label>
              <textarea style={txt} placeholder={t('organization.join.aboutPlaceholder')} value={form.about} onChange={e => set('about', e.target.value)} />
            </div>
          </>
        )}

        <TermsAndAge form={form} set={set} t={t} />

        {error && (
          <p style={{ fontSize: '0.875rem', color: '#DC2626', marginBottom: '1rem' }}>{error}</p>
        )}

        <button
          onClick={isFounding ? handleSubmitFounding : handleSubmitStaff}
          disabled={submitting}
          style={{
            backgroundColor: AMBER, color: NAVY, border: 'none', borderRadius: '8px',
            padding: '13px 30px', fontWeight: 700, fontSize: '1rem',
            cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1,
            fontFamily: FONT_BODY, width: '100%', marginTop: '0.5rem',
          }}
        >
          {submitting ? t('organization.join.settingUpButton') : isFounding ? t('organization.join.createOrgButton') : t('organization.join.joinButton')}
        </button>
      </div>
    </Shell>
  );
}
